import { isEqual, isPlainObject } from "lodash";
import { shallowEqual } from "react-redux";

/**
 * Equality function used by `useAppSelector` for ALL ~1,800 selector call sites.
 *
 * Historically this was plain `lodash.isEqual` (deep structural equality). Deep
 * equality is what lets many selectors that return a *fresh* object/array on every
 * store change (e.g. `{ contacts, chats }` wrappers, `.map`/`.filter` results) avoid
 * re-rendering when the *contents* are unchanged. We must preserve that behavior:
 * weakening it to ref- or shallow-equality would cause extra re-renders (a
 * regression) for those components. So the requirement is a comparator that returns
 * **exactly** the same boolean as `isEqual`, only faster on the common paths.
 *
 * This is that comparator. It is *provably semantically identical* to `isEqual`:
 *
 *   1. `a === b`  → true.  (Reference identity and equal primitives; isEqual agrees.)
 *
 *   2. `shallowEqual(a, b)` → true, **but only when both are plain objects/arrays.**
 *      react-redux's shallowEqual returns true when both are objects with the same
 *      keys AND every top-level value is `Object.is`-equal. For a PLAIN object/array,
 *      "same own-enumerable keys, all values identical" implies deep equality, so
 *      `isEqual` would ALSO return true — the fast win for selectors that wrap
 *      already-stable slice references in a fresh object/array.
 *
 *      ⚠️ This implication FAILS for exotic objects whose meaningful state lives in
 *      internal slots that `Object.keys()` cannot see: `Date`, `Map`, `Set`,
 *      `RegExp`, typed arrays, boxed primitives, class instances, etc. Two Dates
 *      with *different* times both have ZERO own-enumerable keys, so shallowEqual
 *      reports them equal — while `isEqual` correctly compares them by time value
 *      and reports them different. Taking the shallow fast-path for those types
 *      silently swallows state changes: a selector returning `Date` (e.g. the
 *      calendar's `currentDate`) gets pinned to its first value forever and its
 *      subscribers never re-render. So we gate the fast-path on `isShallowSafe`
 *      (plain object or array only) and let everything else fall through to isEqual.
 *
 *   3. Otherwise fall back to the authoritative `isEqual` deep compare — identical
 *      result to the original behavior for every remaining input.
 *
 * With the `isShallowSafe` gate, branch (2) only ever returns true on a strict
 * subset of isEqual's true-set, and branch (3) is isEqual itself, so the returned
 * value equals `isEqual(a, b)` for every possible pair of arguments. The only
 * difference vs. plain isEqual is a cheap shallow pre-pass that avoids the deep walk
 * for the "fresh wrapper of stable references" pattern (very common after the R2
 * selector stabilization).
 */

// The shallowEqual fast-path is a valid proxy for deep isEqual ONLY for plain
// containers (object literals / arrays). Exotic objects carry state in internal
// slots invisible to Object.keys(), so shallowEqual can report two *different*
// values as equal — see the ⚠️ note above. Restrict the fast-path to these.
const isShallowSafe = (v: unknown): boolean => Array.isArray(v) || isPlainObject(v);

export function selectorEqualityFn<T>(a: T, b: T): boolean {
  if (a === b) return true;
  // Fast path only when BOTH sides are plain object/array; otherwise the authoritative
  // deep isEqual (which understands Date/Map/Set/RegExp/… by value) is the arbiter.
  if (isShallowSafe(a) && isShallowSafe(b) && shallowEqual(a, b)) return true;
  return isEqual(a, b);
}
